<template>
  <div class="aptitude-report">
    <h3 class="report-title">傾向レポート(β版)</h3>

    <div v-if="!hasData" class="no-data">
      <p>まだデータがありません。問題に回答すると結果が表示されます。</p>
    </div>

    <div v-else class="report-content">
      <!-- 全体統計 -->
      <div class="report-section">
        <h4>全体統計</h4>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-label">総問題数</div>
            <div class="stat-value">{{ totalQuestions }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">正解率</div>
            <div class="stat-value">{{ correctRate }}%</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">得意</div>
            <div class="stat-value">{{ goodCategory }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">苦手</div>
            <div class="stat-value">{{ badCategory }}</div>
          </div>
        </div>
      </div>

      <!-- 課金すれば見れる旨を表示 -->
      <div v-if="!$globalVar.isYagiPrime">
        <p>ヤギプライム会員になると、詳細な種類別・難易度別のレポートが閲覧できるようになります。より深い分析で学習効率を高めましょう。</p>
      </div>

      <!-- 種類別成績 -->
      <div class="report-section" v-if="typeStats.length > 0">
        <h4>種類別成績</h4>
        <div class="table-container" :class="{ 'blurred': !$globalVar.isYagiPrime }">
          <table class="stats-table">
            <thead>
              <tr>
                <th>種類</th>
                <th>問題数</th>
                <th>正解数</th>
                <th>正解率</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(stat, index) in typeStats" :key="index">
                <td>{{ stat.type }}</td>
                <td>{{ stat.count }}</td>
                <td>{{ stat.correct }}</td>
                <td>{{ stat.rate }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 難易度別成績 -->
      <div class="report-section" v-if="levelStats.length > 0">
        <h4>難易度別成績</h4>
        <div class="table-container" :class="{ 'blurred': !$globalVar.isYagiPrime }">
          <table class="stats-table">
            <thead>
              <tr>
                <th>難易度</th>
                <th>問題数</th>
                <th>正解数</th>
                <th>正解率</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(stat, index) in levelStats" :key="index">
                <td>{{ stat.level }}</td>
                <td>{{ stat.count }}</td>
                <td>{{ stat.correct }}</td>
                <td>{{ stat.rate }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 最近の解答 -->
      <div class="report-section">
        <h4>直近5問の解答</h4>
        <div class="answer-list" :class="{ 'blurred': !$globalVar.isYagiPrime }">
          <div v-for="(item, index) in recentAnswers.slice(0, 5)" :key="index" class="answer-item"
            :class="{ 'correct': item.isCorrect, 'incorrect': !item.isCorrect }">
            <div class="answer-header">
              <span class="answer-type">{{ item.type }}</span>
              <span class="answer-level">難易度: {{ item.level }}</span>
              <span class="answer-result">{{ item.isCorrect ? '正解' : '不正解' }}</span>
            </div>
            <div class="answer-question">{{ truncateText(item.questionText, 100) }}</div>
            <div v-if="item.selectList && item.selectList.length > 0" class="select-list">
              <div v-for="(option, optIndex) in item.selectList" :key="optIndex" class="select-option"
                :class="{ 'selected': option.select_num === item.userAnswer, 'correct-answer': option.select_num === item.answer }">
                {{ option.select_num }}. {{ option.select_text }}
              </div>
            </div>
            <div class="answer-detail">
              <span>回答: {{ item.userAnswer }}</span>
              <span v-if="!item.isCorrect">正解: {{ item.answer }}</span>
            </div>
            <div v-if="item.answer_text">
              <span>{{ item.answer_text }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AptitudeReport',
  data () {
    return {
      aptitudeData: [],
      refreshInterval: null
    }
  },
  computed: {
    hasData () {
      return this.aptitudeData && this.aptitudeData.length > 0
    },
    totalQuestions () {
      return this.aptitudeData.length
    },
    correctAnswers () {
      return this.aptitudeData.filter(item => item.isCorrect).length
    },
    correctRate () {
      if (this.totalQuestions === 0) return 0
      return Math.round((this.correctAnswers / this.totalQuestions) * 100)
    },
    goodCategory () {
      if (!this.hasData || this.typeStats.length === 0) return 'なし'

      // 各カテゴリの中で正答率が最も高いものを探す
      // 問題数が3問以上あるカテゴリのみを対象とする
      const validStats = this.typeStats

      if (validStats.length === 0) {
        // 3問以上解いたカテゴリがなければ、全カテゴリの中から最も正答率が高いものを返す
        // コピーを作成してからソート
        const sortedStats = [...this.typeStats].sort((a, b) => b.rate - a.rate)
        const bestStat = sortedStats[0]
        return `${bestStat.type} (${bestStat.rate}%)`
      }

      // 正答率でソートして、最も高いカテゴリを返す
      // コピーを作成してからソート
      const sortedValidStats = [...validStats].sort((a, b) => {
        // 正答率が同じ場合は問題数が多い方を優先
        if (a.rate === b.rate) {
          return b.count - a.count
        }
        return b.rate - a.rate
      })
      const bestStat = sortedValidStats[0]

      return `${bestStat.type}`
    },
    badCategory () {
      if (!this.hasData || this.typeStats.length === 0) return 'なし'

      // 各カテゴリの中で正答率が最も低いものを探す
      // 問題数が3問以上あるカテゴリのみを対象とする
      const validStats = this.typeStats

      if (validStats.length === 0) {
        // 3問以上解いたカテゴリがなければ、全カテゴリの中から最も正答率が低いものを返す
        const sortedStats = [...this.typeStats].sort((a, b) => a.rate - b.rate)
        const bestStat = sortedStats[0]
        return `${bestStat.type} (${bestStat.rate}%)`
      }

      // 正答率でソートして、最も低いカテゴリを返す
      const sortedValidStats = [...validStats].sort((a, b) => a.rate - b.rate)

      return `${sortedValidStats[0].type}`
    },
    typeStats () {
      if (!this.hasData) return []

      const stats = {}

      this.aptitudeData.forEach(item => {
        if (!stats[item.type]) {
          stats[item.type] = { type: item.type, count: 0, correct: 0 }
        }

        stats[item.type].count++
        if (item.isCorrect) {
          stats[item.type].correct++
        }
      })

      return Object.values(stats).map(stat => {
        return {
          ...stat,
          rate: Math.round((stat.correct / stat.count) * 100)
        }
      }).sort((a, b) => b.count - a.count)
    },
    levelStats () {
      if (!this.hasData) return []

      const stats = {}

      this.aptitudeData.forEach(item => {
        if (!stats[item.level]) {
          stats[item.level] = { level: item.level, count: 0, correct: 0 }
        }

        stats[item.level].count++
        if (item.isCorrect) {
          stats[item.level].correct++
        }
      })

      return Object.values(stats).map(stat => {
        return {
          ...stat,
          rate: Math.round((stat.correct / stat.count) * 100)
        }
      }).sort((a, b) => a.level - b.level)
    },
    recentAnswers () {
      if (!this.hasData) return []

      // タイムスタンプでソートして最新の5件を返す
      return [...this.aptitudeData]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 5)
    }
  },
  methods: {
    loadData () {
      const storedData = localStorage.getItem('aptitudeChatData')
      if (storedData) {
        this.aptitudeData = JSON.parse(storedData)
      }
    },
    truncateText (text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },
    startRefreshInterval () {
      // 1秒ごとにデータを更新
      this.refreshInterval = setInterval(() => {
        this.loadData()
      }, 1000)
    },
    stopRefreshInterval () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
      }
    }
  },
  created () {
    this.loadData()
    this.startRefreshInterval()
  },
  beforeDestroy () {
    this.stopRefreshInterval()
  }
}
</script>

<style scoped>
.aptitude-report {
  padding: 16px;
  border-radius: 8px;
  height: 100%;
  overflow-y: auto;
  max-height: 100%;
  margin: 0 auto;
  max-width: 768px;
}

.report-title {
  font-size: 18px;
  color: #004d40;
  margin-bottom: 16px;
  text-align: center;
  border-bottom: 2px solid #8fbd67;
  padding-bottom: 8px;
}

.no-data {
  text-align: center;
  color: #757575;
  padding: 24px 0;
}

.report-section {
  margin-bottom: 24px;
  background-color: #fff;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
}

.report-section h4 {
  font-size: 16px;
  color: #004d40;
  margin-bottom: 12px;
  border-left: 4px solid #8fbd67;
  padding-left: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.stat-item {
  text-align: center;
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 6px;
}

.stat-label {
  font-size: 14px;
  color: #616161;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #004d40;
}

.table-container {
  overflow-x: auto;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
}

.stats-table th,
.stats-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.stats-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  color: #616161;
}

.answer-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.answer-item {
  padding: 12px;
  border-radius: 6px;
  background-color: #f5f5f5;
  position: relative;
  border-left: 4px solid #bdbdbd;
}

.answer-item.correct {
  border-left-color: #4caf50;
}

.answer-item.incorrect {
  border-left-color: #f44336;
}

.answer-header {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 12px;
}

.answer-type {
  font-weight: bold;
  color: #004d40;
}

.answer-level {
  color: #616161;
}

.answer-result {
  font-weight: bold;
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: auto;
}

.answer-item.correct .answer-result {
  background-color: #4caf50;
}

.answer-item.incorrect .answer-result {
  background-color: #f44336;
}

.answer-question {
  font-size: 14px;
  margin-bottom: 8px;
  color: #212121;
}

.select-list {
  margin: 10px 0;
  background-color: #fff;
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
}

.select-option {
  padding: 4px 8px;
  border-radius: 4px;
}

.select-option.selected {
  background-color: #e8f5e9;
  font-weight: bold;
}

.select-option.correct-answer {
  color: #2e7d32;
  border-left: 3px solid #2e7d32;
}

.answer-detail {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #616161;
  margin-top: 8px;
}

.blurred {
  filter: blur(5px);
  pointer-events: none;
  user-select: none;
}
</style>
